import PageLayout from "@/components/feature/PageLayout";
import PageHero from "@/components/feature/PageHero";
import PageCTA from "@/components/feature/PageCTA";
import Breadcrumb from "@/components/feature/Breadcrumb";
import FAQSection from "@/components/feature/FAQSection";
import AnimatedSection from "@/components/AnimatedSection";

const saasData = {
  hero: {
    badge: "SaaS Development",
    headline: "Build a Scalable SaaS Product",
    subtext: "Multi-tenant SaaS platforms with subscriptions, analytics, user management and scalable architecture built for growth.",
    primaryCTA: "Build Your SaaS",
    secondaryCTA: "Talk to a Laravel Expert",
  },
  services: [
    { title: "SaaS Architecture", desc: "Multi-tenant systems with tenant isolation, custom domains and white-label support.", icon: "ri-building-line" },
    { title: "Multi-tenancy", desc: "Database, subdomain and configuration isolation for each tenant.", icon: "ri-stack-line" },
    { title: "Subscription Management", desc: "Billing cycles, trials, upgrades, downgrades and usage-based pricing.", icon: "ri-vip-crown-line" },
    { title: "Payment Integration", desc: "Stripe, PayPal and ACH with automated invoicing and dunning.", icon: "ri-bank-card-line" },
    { title: "User Management", desc: "Invitations, roles, teams and workspaces with granular permissions.", icon: "ri-team-line" },
    { title: "Admin Dashboard", desc: "Powerful dashboards for super-admins and tenant administrators.", icon: "ri-dashboard-line" },
    { title: "Analytics", desc: "Usage metrics, churn analysis, revenue reports and user engagement.", icon: "ri-bar-chart-line" },
    { title: "Notifications", desc: "Email, SMS and in-app notifications with templates and scheduling.", icon: "ri-notification-3-line" },
    { title: "Scalability", desc: "Auto-scaling infrastructure designed to grow with your user base.", icon: "ri-expand-diagonal-line" },
  ],
  techStack: [
    { name: "Laravel", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/laravel/laravel-original.svg", featured: true },
    { name: "React", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/react/react-original.svg" },
    { name: "PostgreSQL", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/postgresql/postgresql-original.svg" },
    { name: "Redis", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/redis/redis-original.svg" },
    { name: "AWS", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/amazonwebservices/amazonwebservices-original-wordmark.svg" },
    { name: "Stripe", logo: "https://cdn.jsdelivr.net/gh/devicons/devicon@latest/icons/stripe/stripe-original.svg" },
  ],
  faq: [
    { q: "Can you build a multi-tenant SaaS?", a: "Yes. We build SaaS platforms with database-per-tenant or shared-database architecture with full tenant isolation." },
    { q: "How do you handle subscription billing?", a: "We integrate Stripe, PayPal or custom billing with trials, proration, usage metering and automated invoicing." },
    { q: "Can tenants have custom domains?", a: "Yes. We support custom domains, white-label branding and tenant-specific configurations." },
    { q: "Is the platform scalable?", a: "Yes. We design auto-scaling infrastructure on AWS with load balancers, caching and CDN for global reach." },
  ],
};

export default function SaasDevelopmentPage() {
  return (
    <PageLayout>
      <Breadcrumb
        items={[
          { label: "Home", href: "/" },
          { label: "Services", href: "/services" },
          { label: "SaaS Development" },
        ]}
      />

      <PageHero
        badge={saasData.hero.badge}
        headline={saasData.hero.headline}
        subtext={saasData.hero.subtext}
        primaryCTA={saasData.hero.primaryCTA}
        secondaryCTA={saasData.hero.secondaryCTA}
      />

      <section className="py-20 md:py-28 bg-background-50">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">SaaS Development Services</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
            {saasData.services.map((service, i) => (
              <AnimatedSection key={service.title} delay={i * 100}>
                <div className="group p-6 md:p-7 rounded-2xl bg-background-100 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-400 card-lift h-full">
                  <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-accent-50 text-accent-600 mb-5 group-hover:bg-accent-500 group-hover:text-background-50 transition-all duration-300">
                    <span className="w-6 h-6 flex items-center justify-center">
                      <i className={`${service.icon} text-xl`} />
                    </span>
                  </div>
                  <h3 className="text-base font-bold text-foreground-950 mb-2 font-heading">{service.title}</h3>
                  <p className="text-sm text-foreground-600 leading-relaxed">{service.desc}</p>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 md:py-28 bg-background-100">
        <div className="container-astro">
          <AnimatedSection>
            <div className="text-center mb-14 md:mb-18">
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-extrabold text-foreground-950 mb-5 font-heading tracking-tight">Technology Stack</h2>
              <div className="w-16 h-1 bg-accent-500 rounded-full mx-auto" />
            </div>
          </AnimatedSection>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4 md:gap-5">
            {saasData.techStack.map((tech, i) => (
              <AnimatedSection key={tech.name} delay={i * 60}>
                <div className={`flex flex-col items-center gap-3 p-5 md:p-6 rounded-2xl bg-background-50 border border-background-200/60 hover:border-accent-300/30 hover:shadow-lg hover:shadow-accent-500/5 transition-all duration-300 card-lift ${tech.featured ? "ring-2 ring-accent-500/20" : ""}`}>
                  {tech.logo ? (
                    <img src={tech.logo} alt={tech.name} className="w-10 h-10 object-contain" />
                  ) : (
                    <span className="w-10 h-10 flex items-center justify-center text-accent-500">
                      <i className={`${tech.icon} text-2xl`} />
                    </span>
                  )}
                  <span className={`text-sm font-semibold ${tech.featured ? "text-accent-600" : "text-foreground-800"}`}>{tech.name}</span>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      <FAQSection heading="SaaS Development FAQ" items={saasData.faq} />

      <PageCTA
        heading="Have a project in mind?"
        text="Let's build something that scales."
        primaryCTA="Start Your Project"
        secondaryCTA="Talk to a Laravel Expert"
      />
    </PageLayout>
  );
}